import React from "react";
import Contents from "../components/dashboard/Contents";
import DateRangePicker from "../../admin/components/dashboard/customs/DateRangePicker";
import YearSelector from "../../admin/components/dashboard/customs/YearSelector";
import { useDateFilter } from "../../contexts/dashboard/DateFilterContext";
import { useQuery } from "@tanstack/react-query";
import accessURL from "../../components/utils/superAccess";
import { DashboardData } from "../types";

interface ApiResponse {
  status: string;
  code: number;
  data: DashboardData;
}

const dashboard = async (
  startDate?: string,
  endDate?: string,
  year?: number
): Promise<ApiResponse> => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  if (year) params.append("year", year.toString());

  const res = await accessURL(`super-admin/dashboard?${params.toString()}`);
  return {
    status: res.data.status,
    code: res.data.code,
    data: res.data.data,
  };
};

const Dashboard: React.FC = () => {
  const dateFilter = useDateFilter();

  const startDate = dateFilter?.startDate ?? "";
  const endDate = dateFilter?.endDate ?? "";
  const selectedYear = dateFilter?.selectedYear ?? new Date().getFullYear();

  const { data, isLoading, isError, error } = useQuery<ApiResponse>({
    queryKey: ["superAdminDashboardDetails", startDate, endDate, selectedYear],
    queryFn: () => dashboard(startDate, endDate, selectedYear),
    retry: 2, // Retry failed requests up to 2 times
    // Ensure data is valid before considering the query successful
    select: (response) => {
      if (!response?.data) {
        throw new Error("Invalid data received from server");
      }
      return response;
    },
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">Loading dashboard data...</p>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-red-600">
            Error loading dashboard:{" "}
            {(error as Error)?.message || "Something went wrong"}
          </p>
        </div>
      );
    }

    if (!data || !data.data) {
      return (
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">No data available</p>
        </div>
      );
    }

    return <Contents propData={data.data} isLoading={false} />;
  };

  return (
    <section className="px-10 flex flex-col justify-center space-y-10">
      <header>
        <h1 className="text-3xl text-gray-800 capitalize font-bold">
          dashboard
        </h1>
      </header>
      <div className="flex items-center space-x-4 mt-4 py-4 lg:mt-0">
        <DateRangePicker />
        <YearSelector />
      </div>
      {renderContent()}
    </section>
  );
};

export default Dashboard;
